import React, { useState } from "react";
import {
  Modal,
  Button,
  Confirm,
  Icon,
  Form,
  Dropdown,
  Input,
  Label
} from "semantic-ui-react";
import { useDispatch, useSelector, connect } from "react-redux";
import { Formik, Field } from "formik";
import styles from "./ClockIn.module.css";
import {
  generalTasksIdsOptions,
  generalTasksAreas
} from "selectors/generalTasks";
import moment from "moment";
import EmployeeSection from "./component/EmployeesSection";
import { addGeneralTasksClockIn, fetchGeneralTask } from "actions/WorkingTime";
import AreasList from "routes/WorkingTime/components/AreasList";
import DateTimePicker from "./DateTimePicker";

const ClockIn = ({
  params,
  workingTimeCompleted,
  pageActiveTab,
  open,
  onClose,
  areas
}) => {
  const dispatch = useDispatch();
  const [secondOpen, setSecondOpen] = useState(false);
  const generalTasksIds = useSelector(generalTasksIdsOptions);
  const tasksAreas = useSelector(generalTasksAreas);

  const onSubmit = data => {
    dispatch(addGeneralTasksClockIn(data))
      .then(res => {
        if (res) {
          dispatch(
            fetchGeneralTask(params, pageActiveTab, workingTimeCompleted)
          );
        }
      })
      .then(() => onClose());
  };

  return (
    <Modal
      open={open}
      closeOnDimmerClick={false}
      closeOnEscape={false}
      size="large"
      onClick={e => e.stopPropagation()}
      className={styles.root}
    >
      <Modal.Header className={styles.headerWrapper}>
        <div className={styles.header}>Clock In</div>
        <div className={styles.icon}>
          <Icon name="close" onClick={onClose} />
        </div>
      </Modal.Header>
      <Formik
        enableReinitialize
        validateOnChange={false}
        initialValues={{
          generalTaskId: "",
          areas: [],
          startAt: "",
          children: []
        }}
      >
        {({ values, setFieldValue, setValues, dirty, isValid }) => {
          const validation =
            values.generalTaskId &&
            values.children.length &&
            values.children.every(
              item =>
                item.areas.length &&
                item.startAt &&
                item.employeeId &&
                item.generalTaskId
            );

          const areasOptions = values.generalTaskId
            ? tasksAreas[values.generalTaskId]
            : [];

          return (
            <Form>
              <Modal.Content scrolling className={styles.modalContentContainer}>
                <div className={styles.contentHeader}>Select the task</div>
                <Form.Group widths={16}>
                  <Form.Field required width={8}>
                    <label className={styles.formLabel}>Task </label>
                    <Dropdown
                      text={
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px"
                          }}
                        >
                          {generalTasksIds.find(
                            i => i.value === values.generalTaskId
                          )?.text || ""}
                        </span>
                      }
                      search
                      value={values.generalTaskId}
                      icon={<Icon name="chevron down" />}
                      className={styles.dropdownCustomStyle}
                      options={generalTasksIds}
                      onChange={(_, data) => {
                        return (
                          setFieldValue("generalTaskId", data.value),
                          setFieldValue("areas", []),
                          setFieldValue(
                            "children",
                            values.children.map(i => ({
                              ...i,
                              areas: [],
                              generalTaskId: data.value
                            }))
                          )
                        );
                      }}
                    />
                  </Form.Field>
                </Form.Group>
                <div className={styles.contentHeader}>
                  Specify areas, date and time
                </div>
                <Form.Group widths={16}>
                  <Form.Field required width={8}>
                    <label className={styles.formLabel}>Areas </label>
                    <AreasList
                      text={
                        !!values.areas.length && `${values.areas.length} areas`
                      }
                      onChange={(_, data) => {
                        return Array.isArray(data)
                          ? (setFieldValue("areas", [...values.areas, ...data]),
                            setFieldValue(
                              "children",
                              values.children.map(i => ({
                                ...i,
                                areas: [...values.areas, ...data]
                              }))
                            ))
                          : (setFieldValue("areas", [...values.areas, data]),
                            setFieldValue(
                              "children",
                              values.children.map(i => ({
                                ...i,
                                areas: [...values.areas, data]
                              }))
                            ));
                      }}
                      options={areasOptions.filter(
                        a => !values.areas.includes(a.value)
                      )}
                      disabled={!values.generalTaskId}
                    />
                  </Form.Field>
                  <Form.Field required width={8}>
                    <label className={styles.formLabel}>Date and time</label>
                    <div className={styles.calendarWrapper}>
                      <DateTimePicker
                        value={
                          values.startAt
                            ? `${moment(values.startAt).format(
                                "DD/MM/YYYY HH:mm:ss"
                              )}`
                            : null
                        }
                        onChange={value => {
                          const date = new Date(value?.toDate?.().toString());
                          const formattedDate = moment(date).format(
                            "YYYY-MM-DDTHH:mm:ss"
                          );
                          return (
                            setFieldValue("startAt", formattedDate),
                            setFieldValue(
                              "children",
                              values.children.map(i => ({
                                ...i,
                                startAt: formattedDate
                              }))
                            )
                          );
                        }}
                      />
                    </div>
                  </Form.Field>
                </Form.Group>
                <Form.Group>
                  {!!values.areas.length && (
                    <Form.Field>
                      <div className={styles.areasRow}>
                        <span className={styles.areasRowHead}>Areas:</span>
                        {values.areas?.map(item => {
                          return areas.map(({ id, name, parent }) => {
                            return (
                              id === (item?.areaId || item) && (
                                <Label className={styles.addedItem}>
                                  <span className={styles.addedItemValue}>
                                    {" "}
                                    {parent ? `${parent.name}/ ${name}` : name}
                                  </span>
                                  <Icon
                                    className={styles.closeIcon}
                                    name="close"
                                    onClick={() => {
                                      const filteredValues = values.areas?.filter(
                                        id => item !== id
                                      );

                                      setValues({
                                        ...values,
                                        areas: filteredValues,
                                        children: values.children.map(i => ({
                                          ...i,
                                          areas: filteredValues
                                        }))
                                      });
                                    }}
                                  />
                                </Label>
                              )
                            );
                          });
                        })}
                      </div>
                    </Form.Field>
                  )}
                </Form.Group>
                <div className={styles.contentHeader}>Select employees</div>
                <Form.Group widths={16}>
                  <EmployeeSection
                    values={values}
                    areasOptions={areasOptions}
                    setFieldValue={setFieldValue}
                    setValues={setValues}
                  />
                </Form.Group>
              </Modal.Content>
              <Modal.Actions className={styles.actionButtons}>
                <Button
                  content="Cancel"
                  color="green"
                  basic
                  onClick={() => {
                    dirty ? setSecondOpen(true) : onClose();
                  }}
                />
                <Button
                  content="Save"
                  color="green"
                  onClick={() => onSubmit(values.children)}
                  disabled={!validation || !dirty}
                />
              </Modal.Actions>
            </Form>
          );
        }}
      </Formik>
      <Confirm
        className={styles.confirmModal}
        content="Are you sure you want to close modal window"
        open={secondOpen}
        onCancel={() => setSecondOpen(false)}
        onConfirm={() => {
          onClose();
          setSecondOpen(false);
        }}
      />
    </Modal>
  );
};

export default connect((state, props) => {
  return {
    areas: state.areas.list.content
  };
})(ClockIn);
